<template>
  <div v-if="isVisible" :class="$style.modalOverlay" @click="closeModal">
    <div :class="$style.modalContent" @click.stop>
      <div :class="$style.modalHeader">
        <h2 :class="$style.modalTitle">🎨 아티스트 카드 설정</h2>
        <button :class="$style.closeButton" @click="closeModal">×</button>
      </div>

      <div :class="$style.modalBody">
        <!-- 사용자 정보 미리보기 -->
        <div :class="$style.userPreview">
          <h3 :class="$style.sectionTitle">아티스트 정보</h3>
          <div :class="$style.previewCard">
            <div :class="$style.previewImage">
              <img v-if="userProfileImageUrl" :src="userProfileImageUrl" :alt="userStore.nickname || '프로필 이미지'"
                :class="$style.profileImage" @error="handleImageError" />
              <div v-else :class="$style.noImage">
                <span>👤</span>
              </div>
            </div>
            <div :class="$style.previewInfo">
              <div :class="$style.previewNickname">
                <input v-model="editNicknameValue" :class="$style.nicknameInput" type="text" placeholder="아티스트명 입력"
                  maxlength="20" />
              </div>
              <!-- <div :class="$style.previewEmail">
                {{ userStore.email }}
              </div> -->
              <div v-if="userDetailLink" :class="$style.previewLink">
                <span :class="$style.linkIcon">🔗</span>
                {{ userDetailLink }}
              </div>
              <div v-else :class="$style.noLink">X 연동이 필요합니다</div>
            </div>
          </div>
        </div>

        <!-- 연동 상태 표시 -->
        <!-- <div v-if="artistCardConnected" :class="$style.connectionStatus">
          <span :class="$style.statusIcon">✅</span>
          <span>아티스트 카드 연동 완료</span>
        </div> -->

        <!-- 아티스트 타입 선택 섹션 -->
        <div :class="$style.typeSection">
          <h3 :class="$style.sectionTitle">아티스트 타입</h3>

          <!-- 현재 선택된 타입 표시 -->
          <div v-if="
            currentCategory ||
            (currentPositions && currentPositions.length > 0)
          " :class="$style.typeDisplay">
            <div :class="$style.typeDisplayText">
              <strong>선택된 타입:</strong>
              <span :class="$style.typeTag">
                {{ getArtistDisplayName() }}
              </span>
            </div>
          </div>

          <!-- 카테고리 선택 (밴드/개인) -->
          <div :class="$style.categorySection">
            <h4 :class="$style.subSectionTitle">아티스트 분류</h4>
            <div :class="$style.radioGroup">
              <label :class="$style.radioLabel">
                <input type="radio" v-model="currentCategory" value="band" :class="$style.radioInput" />
                <span :class="$style.radioText">밴드</span>
              </label>
              <label :class="$style.radioLabel">
                <input type="radio" v-model="currentCategory" value="individual" :class="$style.radioInput" />
                <span :class="$style.radioText">개인</span>
              </label>
            </div>
          </div>

          <!-- 밴드인 경우 멤버 선택 -->
          <div v-if="currentCategory === 'band'" :class="$style.membersSection">
            <h4 :class="$style.subSectionTitle">밴드 멤버</h4>

            <!-- 기존 멤버 목록 -->
            <div v-if="currentMembers && currentMembers.length > 0" :class="$style.membersList">
              <div v-for="(member, index) in currentMembers" :key="index" :class="$style.memberItem">
                <div :class="$style.memberInfo">
                  <span :class="$style.memberName">{{ member.name }}</span>
                  <span :class="$style.memberPosition">({{ getMemberPosition(member.id) }})</span>
                </div>
                <button @click="removeMember(index)" :class="[$style.priorityButton, $style.removeButton]"
                  type="button">
                  ✕
                </button>
              </div>
            </div>

            <!-- 멤버가 없을 때 안내 메시지 -->
            <div v-else :class="$style.noMembersMessage">
              <div :class="$style.noMembersIcon">🎤</div>
              <p :class="$style.noMembersText">아직 등록된 멤버가 없습니다</p>
              <p :class="$style.noMembersSubText">
                아래에서 멤버를 추가해보세요!
              </p>
            </div>

            <!-- 새 멤버 추가 -->
            <div :class="$style.memberInputContainer">
              <AutoComplete ref="memberAutocompleteRef" v-model="newMemberInput" :suggestions="filteredMembers"
                @complete="searchMembers" @item-select="addMemberFromSuggestion" @keydown="handleMemberKeydown"
                placeholder="멤버 검색 또는 입력 (엔터/쉼표로 추가)" :class="[$style.memberInput, $style.memberAutocomplete]" dropdown
                :forceSelection="false" panelClass="member-autocomplete-panel" />
              <button @click="addMember" :disabled="!newMemberInput || !newMemberInput.trim()"
                :class="$style.addMemberButton" type="button">
                추가
              </button>
            </div>
          </div>

          <!-- 개인 아티스트인 경우 포지션 선택 -->
          <div v-if="currentCategory === 'individual'" :class="$style.positionsSection">
            <h4 :class="$style.subSectionTitle">
              포지션 선택 (복수 선택 가능)
            </h4>
            <div :class="$style.checkboxGroup">
              <label v-for="position in availablePositions" :key="position.value" :class="$style.checkboxLabel">
                <input type="checkbox" :value="position.value" v-model="currentPositions"
                  :class="$style.checkboxInput" />
                <span :class="$style.checkboxText">{{ position.label }}</span>
              </label>
            </div>

            <!-- 선택된 포지션의 우선순위 조정 -->
            <div v-if="currentPositions && currentPositions.length > 1" :class="$style.prioritySection">
              <h5 :class="$style.priorityTitle">
                포지션 우선순위 (드래그로 순서 변경)
              </h5>
              <div :class="$style.priorityList">
                <div v-for="(position, index) in currentPositions" :key="position" :class="$style.priorityItem">
                  <div :class="$style.priorityItemContent">
                    <span :class="$style.priorityNumber">{{ index + 1 }}</span>
                    <span :class="$style.priorityLabel">{{
                      getPositionLabel(position)
                      }}</span>
                  </div>
                  <div :class="$style.priorityControls">
                    <button v-if="index > 0" @click="movePosition(index, index - 1)" :class="$style.priorityButton"
                      type="button">
                      ↑
                    </button>
                    <button v-if="index < currentPositions.length - 1" @click="movePosition(index, index + 1)"
                      :class="$style.priorityButton" type="button">
                      ↓
                    </button>
                    <button @click="removePosition(position)" :class="[$style.priorityButton, $style.removeButton]"
                      type="button">
                      ✕
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 소속 밴드 (개인 타입일 때만 표시) -->
          <div v-if="currentCategory === 'individual'" :class="$style.bandsSection">
            <h4 :class="$style.subSectionTitle">소속 밴드 <span :class="$style.optionalText">(선택)</span></h4>

            <!-- 기존 밴드 목록 -->
            <div v-if="currentBands && currentBands.length > 0" :class="$style.membersList">
              <div v-for="(band, index) in currentBands" :key="index" :class="$style.memberItem">
                <div :class="$style.memberInfo">
                  <span :class="$style.memberName">{{ band.name }}</span>
                </div>
                <button @click="removeBand(index)" :class="[$style.priorityButton, $style.removeButton]" type="button">
                  ✕
                </button>
              </div>
            </div>

            <!-- 밴드가 없을 때 안내 메시지 -->
            <div v-else :class="$style.noMembersMessage">
              <div :class="$style.noMembersIcon">🎸</div>
              <p :class="$style.noMembersText">소속 밴드가 없습니다</p>
              <p :class="$style.noMembersSubText">
                아래에서 소속 밴드를 추가해보세요!
              </p>
            </div>

            <!-- 새 밴드 추가 -->
            <div :class="$style.memberInputContainer">
              <AutoComplete ref="bandAutocompleteRef" v-model="newBandInput" :suggestions="filteredBands"
                @complete="searchBands" @item-select="addBandFromSuggestion" @keydown="handleBandKeydown"
                placeholder="밴드 검색 또는 입력 (엔터/쉼표로 추가)" :class="[$style.memberInput, $style.memberAutocomplete]" dropdown
                :forceSelection="false" panelClass="band-autocomplete-panel" />
              <button @click="addBand" :disabled="!newBandInput || !newBandInput.trim()" :class="$style.addMemberButton"
                type="button">
                추가
              </button>
            </div>
          </div>

          <!-- 한줄소개 섹션 -->
          <div :class="$style.descriptionSection">
            <h4 :class="$style.subSectionTitle">
              아티스트 소개 <span :class="$style.optionalText">(선택)</span>
            </h4>
            <input v-model="currentDescription" :class="$style.descriptionInput" type="text"
              placeholder="간단한 한줄소개를 입력하세요 (예: 감성 어쿠스틱 싱어송라이터)" :disabled="artistCardApplying" maxlength="100" />
            <div :class="[
              $style.charCount,
              { [$style.charCountWarning]: currentDescription.length > 80 },
              { [$style.charCountDanger]: currentDescription.length >= 100 },
            ]">
              {{ currentDescription.length }}/100
            </div>
          </div>

          <!-- SNS 링크 섹션 -->
          <div :class="$style.snsSection">
            <h4 :class="$style.subSectionTitle">
              SNS 링크 <span :class="$style.optionalText">(선택)</span>
            </h4>

            <!-- SNS 리스트 -->
            <div v-for="(sns, index) in snsList" :key="index" :class="$style.snsRow">
              <span :class="$style.snsTypeLabel">{{ getSnsLabel(sns.type) }}</span>
              <input v-model="sns.url" :class="$style.snsInput" type="url" :placeholder="getSnsPlaceholder(sns.type)"
                :disabled="artistCardApplying" />
              <button type="button" :class="$style.snsRemoveBtn" @click="removeSns(index)"
                :disabled="artistCardApplying">
                ✕
              </button>
            </div>

            <!-- SNS 타입 선택 커스텀 드롭다운 -->
            <div :class="$style.snsDropdownWrapper">
              <button type="button" :class="$style.snsDropdownToggle" @click="toggleSnsDropdown"
                :disabled="artistCardApplying">
                + SNS 추가
                <span :class="[$style.dropdownArrow, { [$style.dropdownArrowOpen]: snsDropdownOpen }]">▼</span>
              </button>
              <div v-if="snsDropdownOpen" :class="$style.snsDropdownMenu">
                <button v-for="opt in availableSnsOptions" :key="opt.value" type="button"
                  :class="$style.snsDropdownItem" @click="selectSnsType(opt.value)">
                  {{ opt.label }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- 아티스트 카드 적용/업데이트 섹션 -->
        <div :class="$style.cardSection">
          <h3 :class="$style.sectionTitle">업데이트 시 주의사항!</h3>
          <p :class="$style.sectionDescription">
            아티스트명이 각 공연에 등록된 아티스트명과 정확히 일치해야 공연횟수가
            집계됩니다.
          </p>

          <button :class="[
            $style.cardButton,
            { [$style.updateButton]: artistCardConnected },
          ]" @click="applyArtistCard" :disabled="artistCardApplying || !userDetailLink || !currentSelectedType
            ">
            <span v-if="artistCardApplying">
              {{ artistCardConnected ? "업데이트 중..." : "생성 중..." }}
            </span>
            <span v-else-if="!currentSelectedType">먼저 아티스트 타입을 선택해주세요</span>
            <span v-else>
              {{
                artistCardConnected
                  ? "🔄 아티스트 카드 업데이트"
                  : "✨ 아티스트 카드 생성하기"
              }}
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  collection,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { ref as storageRef } from "firebase/storage";
import { getDownloadURL } from "@/utils/storageWithNavigation";
import { firestore, storage } from "/firebaseConfig";
import { createArtist as createArtistApi, updateArtist as updateArtistApi, renameArtist } from "@/utils/write/artist";
import { setNickname } from "@/utils/write/user";
import { copyStorageFile } from "@/utils/write/storage";
import AutoComplete from "primevue/autocomplete";
import { trackActivity, ACTIVITY_TYPES } from "@/utils/activityTracker";
import { ARTIST_POSITION_OPTIONS, getArtistTypeLabel } from "@/utils/artistTypes";

export default {
  name: "ArtistCardModal",
  components: {
    AutoComplete,
  },
  props: {
    isVisible: {
      type: Boolean,
      default: false,
    },
    userDetailLink: {
      type: String,
      default: null,
    },
    userStore: {
      type: Object,
      required: true,
    },
  },
  emits: ["close", "artist-card-updated"],
  data() {
    return {
      selectedArtistCategory: null, // DB에서 로드된 기존 카테고리
      selectedArtistPositions: [], // DB에서 로드된 기존 포지션들
      selectedArtistMembers: [], // DB에서 로드된 기존 멤버들
      currentCategory: null, // 현재 UI에서 선택된 카테고리
      currentPositions: [], // 현재 UI에서 선택된 포지션들
      currentMembers: [], // 현재 UI에서 선택된 멤버들 (형식: [{id, name}, ...])
      currentBands: [], // 현재 UI에서 선택된 소속 밴드들 (형식: [{id, name}, ...])
      artistsWithTypes: [], // 아티스트 ID, name, type 정보
      justSelectedFromSuggestion: false, // 방금 드롭다운에서 선택했는지 여부
      currentDescription: "", // 현재 UI에서 입력된 상세설명
      snsList: [], // SNS 링크 배열 [{ type: 'youtube', url: '' }, ...]
      snsDropdownOpen: false, // SNS 드롭다운 열림 상태
      snsTypeOptions: [
        { value: "youtube", label: "유튜브" },
        { value: "instagram", label: "인스타그램" },
        { value: "twitter", label: "X (트위터)" },
        { value: "tiktok", label: "틱톡" },
        { value: "soundcloud", label: "사운드클라우드" },
      ],
      newMemberInput: "", // 새 멤버 입력란
      filteredMembers: [], // 필터링된 멤버 후보 목록
      newBandInput: "", // 새 밴드 입력란
      filteredBands: [], // 필터링된 밴드 후보 목록
      allArtists: [], // 모든 아티스트 목록 (AutoComplete용)
      artistCardApplying: false,
      artistCardConnected: false,
      userProfileImageUrl: null,
      imageLoading: false,
      // 닉네임 편집 관련
      editNicknameValue: "",
    };
  },
  computed: {
    availablePositions() {
      return ARTIST_POSITION_OPTIONS;
    },
    currentSelectedType() {
      // 버튼 비활성화를 위한 computed 속성
      if (this.currentCategory === "band") {
        return "band";
      } else if (
        this.currentCategory === "individual" &&
        this.currentPositions.length > 0
      ) {
        return this.currentPositions[0]; // 첫 번째 포지션을 대표로 사용
      }
      return null;
    },
    // SNS 타입 옵션 (동일 타입도 추가 가능)
    availableSnsOptions() {
      return this.snsTypeOptions;
    },
  },
  watch: {
    isVisible: {
      immediate: true,
      async handler(newVal) {
        if (newVal) {
          // 모달이 열릴 때 배경 스크롤 비활성화
          document.body.style.overflow = "hidden";
          if (this.userDetailLink) {
            await this.loadData();
          }
        } else {
          // 모달이 닫힐 때 배경 스크롤 복원
          document.body.style.overflow = "";
        }
      },
    },
    userDetailLink: {
      immediate: true,
      async handler(newVal) {
        if (newVal && this.isVisible) {
          await this.loadData();
        }
      },
    },
    currentPositions(newPositions, oldPositions) {
      // 포지션이 4개를 초과하는 경우 제한
      if (newPositions.length > 4) {
        this.$alert("포지션은 최대 4개까지만 설정할 수 있습니다.");
        // 이전 상태로 되돌림 (다음 tick에서 실행하여 무한 루프 방지)
        this.$nextTick(() => {
          this.currentPositions = [...oldPositions];
        });
      }
    },
  },
  beforeUnmount() {
    // 컴포넌트가 제거될 때 스크롤 복원
    document.body.style.overflow = "";
  },
  methods: {
    closeModal() {
      this.$emit("close");
    },

    // SNS 관련 메서드
    addSns() {
      this.snsList.push({ type: "", url: "" });
    },

    removeSns(index) {
      this.snsList.splice(index, 1);
    },

    getSnsPlaceholder(type) {
      const placeholders = {
        youtube: "https://www.youtube.com/@channelname",
        instagram: "https://www.instagram.com/username",
        twitter: "https://x.com/username",
        tiktok: "https://www.tiktok.com/@username",
        soundcloud: "https://soundcloud.com/username",
        other: "https://...",
      };
      return placeholders[type] || "URL을 입력하세요";
    },

    getSnsLabel(type) {
      const labels = {
        youtube: "유튜브",
        instagram: "인스타그램",
        twitter: "X (트위터)",
        tiktok: "틱톡",
        soundcloud: "사운드클라우드",
        other: "기타",
      };
      return labels[type] || type;
    },

    toggleSnsDropdown() {
      this.snsDropdownOpen = !this.snsDropdownOpen;
    },

    selectSnsType(type) {
      this.snsList.push({ type, url: "" });
      this.snsDropdownOpen = false;
    },

    async loadData() {
      // artistsWithTypes가 먼저 로드되어야 멤버/밴드 정보를 올바르게 변환할 수 있음
      await this.loadAllArtists();

      await Promise.all([
        this.loadUserArtistType(),
        this.checkArtistCardConnection(),
        this.loadUserProfileImage(),
      ]);
    },

    async loadUserArtistType() {
      if (!this.userDetailLink) {
        this.selectedArtistCategory = null;
        this.selectedArtistPositions = [];
        this.selectedArtistMembers = [];
        this.currentCategory = null;
        this.currentPositions = [];
        this.currentMembers = [];
        this.currentBands = [];
        this.currentDescription = "";
        this.snsList = [];
        this.editNicknameValue = "";
        return;
      }

      try {
        const artistsQuery = query(
          collection(firestore, "artists"),
          where("detailLink", "==", this.userDetailLink)
        );

        const artistsSnapshot = await getDocs(artistsQuery);
        if (!artistsSnapshot.empty) {
          const artistData = artistsSnapshot.docs[0].data();

          // 기존 아티스트의 이름을 닉네임 필드에 설정
          this.editNicknameValue = artistData.name || this.userStore.nickname || "";

          // 새로운 구조 (category + type 배열)
          if (artistData.category) {
            this.selectedArtistCategory = artistData.category;
            this.currentCategory = artistData.category;
            this.currentDescription = artistData.description || "";
            // SNS 리스트 로드 (기존 youtubeChannel, instagram 필드 호환 + 새로운 snsList 형식)
            this.snsList = [];
            if (artistData.snsList && Array.isArray(artistData.snsList)) {
              this.snsList = artistData.snsList.map((s) => ({ ...s }));
            } else {
              if (artistData.youtubeChannel) {
                this.snsList.push({ type: "youtube", url: artistData.youtubeChannel });
              }
              if (artistData.instagram) {
                this.snsList.push({ type: "instagram", url: artistData.instagram });
              }
            }

            if (
              artistData.category === "individual" &&
              Array.isArray(artistData.type)
            ) {
              // 'mix'를 'other'로 변환 (동일한 값으로 처리)
              const normalizedTypes = artistData.type.map((t) =>
                t === "mix" ? "other" : t
              );
              this.selectedArtistPositions = [...normalizedTypes];
              this.currentPositions = [...normalizedTypes];
              this.selectedArtistMembers = [];
              this.currentMembers = [];

              // 소속 밴드 ID를 {id, name} 형식으로 변환
              const myBandsData = artistData.myBands || artistData.MyBands || [];
              if (Array.isArray(myBandsData) && myBandsData.length > 0) {
                const bandObjects = myBandsData.map(bandId => {
                  const band = this.artistsWithTypes.find(a => a.id === bandId);
                  return band ? { id: band.id, name: band.name } : null;
                }).filter(b => b !== null);
                this.currentBands = [...bandObjects];
              } else {
                this.currentBands = [];
              }
            } else if (artistData.category === "band") {
              this.selectedArtistPositions = [];
              this.currentPositions = [];
              // 멤버 ID를 {id, name} 형식으로 변환
              if (artistData.members && Array.isArray(artistData.members)) {
                const memberObjects = artistData.members.map(memberId => {
                  const artist = this.artistsWithTypes.find(a => a.id === memberId);
                  return artist ? { id: artist.id, name: artist.name } : null;
                }).filter(m => m !== null);
                this.selectedArtistMembers = memberObjects;
                this.currentMembers = [...memberObjects];
              } else {
                this.selectedArtistMembers = [];
                this.currentMembers = [];
              }
            } else {
              this.selectedArtistPositions = [];
              this.currentPositions = [];
              this.selectedArtistMembers = [];
              this.currentMembers = [];
              this.currentBands = [];
            }
          }
          // 기존 구조 호환성 (type 필드만 있는 경우)
          else if (artistData.type) {
            let types = Array.isArray(artistData.type)
              ? artistData.type
              : [artistData.type];

            // 'mix'를 'other'로 변환 (동일한 값으로 처리)
            types = types.map((t) => (t === "mix" ? "other" : t));

            if (types.includes("band")) {
              this.selectedArtistCategory = "band";
              this.currentCategory = "band";
              this.selectedArtistPositions = [];
              this.currentPositions = [];
              // 멤버 ID를 {id, name} 형식으로 변환
              if (artistData.members && Array.isArray(artistData.members)) {
                const memberObjects = artistData.members.map(memberId => {
                  const artist = this.artistsWithTypes.find(a => a.id === memberId);
                  return artist ? { id: artist.id, name: artist.name } : null;
                }).filter(m => m !== null);
                this.selectedArtistMembers = memberObjects;
                this.currentMembers = [...memberObjects];
              } else {
                this.selectedArtistMembers = [];
                this.currentMembers = [];
              }
            } else {
              this.selectedArtistCategory = "individual";
              this.currentCategory = "individual";
              this.selectedArtistPositions = [...types];
              this.currentPositions = [...types];
              this.selectedArtistMembers = [];
              this.currentMembers = [];
            }
            this.currentDescription = artistData.description || "";
            // SNS 리스트 로드 (기존 youtubeChannel, instagram 필드 호환)
            this.snsList = [];
            if (artistData.snsList && Array.isArray(artistData.snsList)) {
              this.snsList = artistData.snsList.map((s) => ({ ...s }));
            } else {
              if (artistData.youtubeChannel) {
                this.snsList.push({ type: "youtube", url: artistData.youtubeChannel });
              }
              if (artistData.instagram) {
                this.snsList.push({ type: "instagram", url: artistData.instagram });
              }
            }
          }
          // 아무것도 없는 경우
          else {
            this.selectedArtistCategory = null;
            this.selectedArtistPositions = [];
            this.selectedArtistMembers = [];
            this.currentCategory = null;
            this.currentPositions = [];
            this.currentMembers = [];
            this.currentDescription = "";
            this.snsList = [];
          }
        } else {
          // 아티스트가 없는 경우 (신규 생성)
          this.selectedArtistCategory = null;
          this.selectedArtistPositions = [];
          this.selectedArtistMembers = [];
          this.currentCategory = null;
          this.currentPositions = [];
          this.currentMembers = [];
          this.currentDescription = "";
          this.snsList = [];
          this.editNicknameValue = this.userStore.nickname || "";
        }
      } catch (error) {
        console.error("사용자 아티스트 타입 로드 오류:", error);
        this.selectedArtistCategory = null;
        this.selectedArtistPositions = [];
        this.selectedArtistMembers = [];
        this.currentCategory = null;
        this.currentPositions = [];
        this.currentMembers = [];
        this.currentDescription = "";
        this.snsList = [];
      }
    },

    async checkArtistCardConnection() {
      if (!this.userDetailLink) {
        this.artistCardConnected = false;
        return;
      }

      try {
        const artistsQuery = query(
          collection(firestore, "artists"),
          where("detailLink", "==", this.userDetailLink)
        );

        const artistsSnapshot = await getDocs(artistsQuery);
        this.artistCardConnected = !artistsSnapshot.empty;
      } catch (error) {
        console.error("아티스트 카드 연동 상태 확인 오류:", error);
        this.artistCardConnected = false;
      }
    },

    getTypeDisplayName(type) {
      return getArtistTypeLabel(type);
    },

    getArtistDisplayName() {
      if (this.currentCategory === "band") {
        return "밴드";
      } else if (
        this.currentCategory === "individual" &&
        this.currentPositions.length > 0
      ) {
        return this.currentPositions
          .map((pos) => this.getTypeDisplayName(pos))
          .join(" / ");
      }
      return "";
    },

    getPositionLabel(position) {
      return this.getTypeDisplayName(position);
    },

    movePosition(fromIndex, toIndex) {
      const positions = [...this.currentPositions];
      [positions[fromIndex], positions[toIndex]] = [
        positions[toIndex],
        positions[fromIndex],
      ];
      this.currentPositions = positions;
    },

    removePosition(position) {
      const index = this.currentPositions.indexOf(position);
      if (index > -1) {
        this.currentPositions.splice(index, 1);
      }
    },

    // 멤버 추가
    addMember() {
      const memberName = this.newMemberInput?.trim();
      if (!memberName) return;

      const artist = this.artistsWithTypes.find(a => a.name?.trim() === memberName);

      if (artist) {
        // 아티스트를 찾은 경우
        if (!this.currentMembers.some(m => m.id === artist.id)) {
          // 아직 선택되지 않은 경우 추가
          this.currentMembers.push({ id: artist.id, name: artist.name });
        }
        // 이미 선택된 경우에도 input은 clear
        this.newMemberInput = "";
      } else {
        // 등록되지 않은 아티스트인 경우 경고
        console.error("Artist not found in artistsWithTypes:", memberName);
        this.$alert("등록되지 않은 아티스트입니다. 검색 목록에서 선택해주세요.");
        this.newMemberInput = "";
      }
    },

    // 멤버 제거
    removeMember(index) {
      if (index >= 0 && index < this.currentMembers.length) {
        this.currentMembers.splice(index, 1);
      }
    },

    // 밴드 검색
    searchBands(event) {
      const query = event.query.toLowerCase();
      if (!query) {
        this.filteredBands = [];
        return;
      }

      // 밴드 타입만 필터링
      const bands = this.artistsWithTypes.filter(artist => {
        const isBand = artist.type &&
          (Array.isArray(artist.type) ? artist.type.includes('band') : artist.type === 'band');
        return isBand && artist.name.toLowerCase().includes(query);
      });

      const selectedBandIds = this.currentBands.map(b => b.id);
      this.filteredBands = bands
        .filter(band => !selectedBandIds.includes(band.id))
        .map(band => band.name);

      // 검색 후 드롭다운 너비 조정
      this.adjustDropdownWidth();
    },

    // 자동완성에서 밴드 선택
    addBandFromSuggestion(event) {
      const bandName = event.value?.trim();

      // 즉시 플래그 설정하고 input 초기화
      this.justSelectedFromSuggestion = true;
      this.newBandInput = "";
      this.filteredBands = [];

      const band = this.artistsWithTypes.find(a => {
        const isBand = a.type && (Array.isArray(a.type) ? a.type.includes('band') : a.type === 'band');
        return isBand && a.name?.trim() === bandName;
      });

      if (band) {
        if (!this.currentBands.some(b => b.id === band.id)) {
          this.currentBands.push({ id: band.id, name: band.name });
        }
      } else {
        console.error("Band not found in artistsWithTypes:", bandName);
      }

      // 플래그를 해제
      setTimeout(() => {
        this.justSelectedFromSuggestion = false;
      }, 300);
    },

    // 키보드 이벤트 처리 (밴드)
    handleBandKeydown(event) {
      // 드롭다운에서 방금 선택한 경우 키보드 이벤트 무시
      if (this.justSelectedFromSuggestion) {
        return;
      }

      if (event.key === "Enter" || event.key === ",") {
        event.preventDefault();
        this.addBand();
      }
    },

    // 밴드 추가
    addBand() {
      const bandName = this.newBandInput?.trim();
      if (!bandName) return;

      const band = this.artistsWithTypes.find(a => {
        const isBand = a.type && (Array.isArray(a.type) ? a.type.includes('band') : a.type === 'band');
        return isBand && a.name?.trim() === bandName;
      });

      if (band) {
        if (!this.currentBands.some(b => b.id === band.id)) {
          this.currentBands.push({ id: band.id, name: band.name });
        }
        this.newBandInput = "";
        this.filteredBands = [];
      } else {
        this.$alert("등록되지 않은 밴드입니다. 검색 목록에서 선택해주세요.");
        this.newBandInput = "";
        this.filteredBands = [];
      }
    },

    // 밴드 제거
    removeBand(index) {
      if (index >= 0 && index < this.currentBands.length) {
        this.currentBands.splice(index, 1);
      }
    },

    // 모든 아티스트 목록 로드
    async loadAllArtists() {
      try {
        const artistsSnapshot = await getDocs(collection(firestore, "artists"));
        this.artistsWithTypes = artistsSnapshot.docs
          .map((doc) => {
            const data = doc.data();
            return {
              id: doc.id, // 문서 ID 추가
              name: data.name,
              type: data.type,
              category: data.category,
            };
          })
          .filter((artist) => artist.name && artist.name.trim() !== "");

        this.allArtists = this.artistsWithTypes.map((artist) => artist.name);

      } catch (error) {
        console.error("아티스트 목록 로드 오류:", error);
        this.allArtists = [];
        this.artistsWithTypes = [];
      }
    },

    // 멤버 검색
    searchMembers(event) {
      const query = event.query.toLowerCase();
      if (!query) {
        this.filteredMembers = [];
        return;
      }

      const selectedMemberIds = this.currentMembers.map(m => m.id);

      this.filteredMembers = this.allArtists.filter((artistName) => {
        const artist = this.artistsWithTypes.find(a => a.name === artistName);
        return artistName.toLowerCase().includes(query) &&
          artist &&
          !selectedMemberIds.includes(artist.id);
      });

      // 검색 후 드롭다운 너비 조정
      this.adjustDropdownWidth();
    },

    // 자동완성에서 멤버 선택
    addMemberFromSuggestion(event) {
      const memberName = event.value?.trim();

      // 즉시 플래그 설정하고 input 초기화
      this.justSelectedFromSuggestion = true;
      this.newMemberInput = "";
      this.filteredMembers = [];

      const artist = this.artistsWithTypes.find(a => a.name?.trim() === memberName);

      if (artist) {
        if (!this.currentMembers.some(m => m.id === artist.id)) {
          this.currentMembers.push({ id: artist.id, name: artist.name });
        }
      } else {
        console.error("Artist not found in artistsWithTypes:", memberName);
      }

      // 플래그를 해제
      setTimeout(() => {
        this.justSelectedFromSuggestion = false;
      }, 300);
    },

    // 키보드 이벤트 처리
    handleMemberKeydown(event) {
      // 드롭다운에서 방금 선택한 경우 키보드 이벤트 무시
      if (this.justSelectedFromSuggestion) {
        return;
      }

      if (event.key === "Enter" || event.key === ",") {
        event.preventDefault();
        this.addMember();
      }
    },

    // 멤버의 포지션 정보 가져오기 (ID 기반)
    getMemberPosition(memberId) {
      const artist = this.artistsWithTypes.find(
        (artist) => artist.id === memberId
      );
      if (!artist) return "아티스트";

      // 새로운 구조 (category + type 배열)
      if (artist.category === "individual" && Array.isArray(artist.type)) {
        return artist.type
          .map((pos) => this.getTypeDisplayName(pos))
          .join(" / ");
      } else if (artist.category === "band") {
        return "밴드";
      }

      // 기존 구조 호환성
      if (artist.type) {
        const types = Array.isArray(artist.type) ? artist.type : [artist.type];
        if (types.includes("band")) {
          return "밴드";
        } else {
          return types.map((pos) => this.getTypeDisplayName(pos)).join(" / ");
        }
      }

      return "아티스트";
    },

    async applyArtistCard() {
      if (!this.userDetailLink) {
        this.$alert("먼저 X 연동을 통해 상세 정보를 가져와주세요.");
        return;
      }

      try {
        this.artistCardApplying = true;

        // 닉네임이 변경되었으면 먼저 저장
        const newNickname = this.editNicknameValue.trim();
        if (newNickname && newNickname !== this.userStore.nickname) {
          await setNickname(this.userStore.email, newNickname);
          this.userStore.setNickname(newNickname);
        }

        // 현재 사용자 정보 가져오기
        const { getUserInfo } = await import("@/utils/read/user");
        const userData = await getUserInfo(this.userStore.email);
        let userProfileImg = userData?.img || null;

        // 동일한 detailLink를 가진 기존 아티스트 찾기
        const artistsQuery = query(
          collection(firestore, "artists"),
          where("detailLink", "==", this.userDetailLink)
        );

        const artistsSnapshot = await getDocs(artistsQuery);

        if (!artistsSnapshot.empty) {
          // 기존 아티스트 업데이트
          const existingArtistDoc = artistsSnapshot.docs[0];
          const existingArtistData = existingArtistDoc.data();
          const oldArtistName = existingArtistData.name;

          // 타입 정보 구성
          let typeInfo;
          if (this.currentCategory === "band") {
            typeInfo = ["band"];
          } else {
            typeInfo = [...this.currentPositions];
          }

          const updateData = {
            type: typeInfo, // 배열로 저장
            category: this.currentCategory, // 카테고리 저장
            description: this.currentDescription.trim() || null, // 상세설명 저장
            snsList: this.snsList.filter((s) => s.type && s.url.trim()).map((s) => ({ type: s.type, url: s.url.trim() })),
            // 수정자 정보 추가 (기존 등록자 정보는 유지)
            lastModifiedBy: this.userStore.email,
            lastModifiedByNickname: this.userStore.nickname,
          };

          // 밴드인 경우 멤버 정보도 포함 (ID만 저장)
          if (
            this.currentCategory === "band" &&
            this.currentMembers.length > 0
          ) {
            updateData.members = this.currentMembers.map(m => m.id);
          }

          // 소속 밴드 정보 (개인 타입일 때)
          if (this.currentCategory === "individual") {
            updateData.myBands = this.currentBands.map(b => b.id);
          }

          // name은 renameArtist에서 처리하므로 여기서는 제외
          // (renameArtist가 oldName으로 문서를 찾기 때문)

          updateData.userEmail = this.userStore.email;

          // 프로필 이미지가 있으면 artist/ 폴더로 복사하고 img 필드에 저장
          if (userProfileImg) {
            try {
              // profile/ 폴더에서 artist/ 폴더로 파일 복사
              const sourcePath = `profile/${userProfileImg}`;
              const destPath = `artist/${userProfileImg}`;
              await copyStorageFile(sourcePath, destPath);
              // img 필드에 파일명 저장
              updateData.img = userProfileImg;
            } catch (error) {
              console.error("프로필 이미지 복사 오류:", error);
              // 복사 실패해도 계속 진행 (이미 artist/에 있을 수도 있음)
              updateData.img = userProfileImg;
            }
          }

          // 아티스트명이 변경된 경우 먼저 관련 컬렉션 모두 업데이트
          // (아티스트 문서의 name 변경도 renameArtist에서 처리)
          if (oldArtistName && this.userStore.nickname && oldArtistName !== this.userStore.nickname) {
            try {
              await renameArtist(oldArtistName, this.userStore.nickname);
            } catch (renameError) {
              console.error("아티스트 이름 변경 오류:", renameError);
              throw renameError; // 이름 변경 실패 시 전체 실패 처리
            }
          }

          await updateArtistApi(existingArtistDoc.id, updateData);

          this.$alert("아티스트 카드가 업데이트되었습니다!");
        } else {
          // 새로운 아티스트 생성
          const artistName = this.editNicknameValue.trim() || this.userStore.nickname || this.userStore.email;

          // 타입 정보 구성
          let typeInfo;
          if (this.currentCategory === "band") {
            typeInfo = ["band"];
          } else {
            typeInfo = [...this.currentPositions];
          }

          const newArtistData = {
            detailLink: this.userDetailLink,
            type: typeInfo, // 배열로 저장
            category: this.currentCategory, // 카테고리 저장
            description: this.currentDescription.trim() || null, // 상세설명 저장
            snsList: this.snsList.filter((s) => s.type && s.url.trim()).map((s) => ({ type: s.type, url: s.url.trim() })),
            userEmail: this.userStore.email,
            // 등록자 정보 추가
            regId: this.userStore.email,
            regNickname: this.userStore.nickname,
            createdBy: this.userStore.email,
            createdByNickname: this.userStore.nickname,
            approved: true, // MyProfile에서 생성하는 아티스트는 자동 승인
            registrationSource: "my-profile", // 등록 경로 구분
          };

          // 밴드인 경우 멤버 정보도 포함 (ID만 저장)
          if (
            this.currentCategory === "band" &&
            this.currentMembers.length > 0
          ) {
            newArtistData.members = this.currentMembers.map(m => m.id);
          }

          // 개인인 경우 소속 밴드 정보도 포함 (ID만 저장)
          if (
            this.currentCategory === "individual" &&
            this.currentBands.length > 0
          ) {
            newArtistData.myBands = this.currentBands.map(b => b.id);
          }

          // 프로필 이미지가 있으면 artist/ 폴더로 복사하고 img 필드에 저장
          if (userProfileImg) {
            try {
              // profile/ 폴더에서 artist/ 폴더로 파일 복사
              const sourcePath = `profile/${userProfileImg}`;
              const destPath = `artist/${userProfileImg}`;
              await copyStorageFile(sourcePath, destPath);
              // img 필드에 파일명 저장
              newArtistData.img = userProfileImg;
            } catch (error) {
              console.error("프로필 이미지 복사 오류:", error);
              // 복사 실패해도 계속 진행 (이미 artist/에 있을 수도 있음)
              newArtistData.img = userProfileImg;
            }
          }

          await createArtistApi(artistName, newArtistData);

          this.$alert("새로운 아티스트 카드가 생성되었습니다!");
        }

        this.artistCardConnected = true;
        this.selectedArtistCategory = this.currentCategory; // DB 상태도 업데이트
        this.selectedArtistPositions = [...this.currentPositions];
        this.selectedArtistMembers = [...this.currentMembers];
        this.$emit("artist-card-updated");

        // 아티스트 카드 설정 활동 기록
        trackActivity(ACTIVITY_TYPES.ARTIST_CARD_UPDATE, {
          metadata: {
            category: this.currentCategory,
            isNew: artistsSnapshot.empty,
          },
        });

        // 업데이트 완료 후 모달 닫기
        setTimeout(() => {
          this.$emit("close");
        }, 500); // 0.5초 후 닫기 (사용자가 성공 메시지를 볼 수 있도록)
      } catch (error) {
        console.error("아티스트 카드 적용 오류:", error);
        this.$alert("아티스트 카드 적용 중 오류가 발생했습니다.");
      } finally {
        this.artistCardApplying = false;
      }
    },

    async loadUserProfileImage() {
      if (!this.userStore.email) {
        this.userProfileImageUrl = null;
        return;
      }

      try {
        this.imageLoading = true;

        // 사용자 문서에서 이미지 파일명 가져오기
        const { getUserInfo } = await import("@/utils/read/user");
        const userData = await getUserInfo(this.userStore.email);

        if (userData) {
          const userProfileImg = userData.img;

          if (userProfileImg) {
            // Firebase Storage에서 이미지 URL 가져오기
            const imageRef = storageRef(storage, `profile/${userProfileImg}`);
            const imageUrl = await getDownloadURL(imageRef);
            this.userProfileImageUrl = imageUrl;
          } else {
            this.userProfileImageUrl = null;
          }
        } else {
          this.userProfileImageUrl = null;
        }
      } catch (error) {
        console.error("사용자 프로필 이미지 로드 오류:", error);
        this.userProfileImageUrl = null;
      } finally {
        this.imageLoading = false;
      }
    },

    handleImageError() {
      // console.log("프로필 이미지 로드 실패");
      this.userProfileImageUrl = null;
    },

    adjustDropdownWidth() {
      this.$nextTick(() => {
        // 멤버 AutoComplete 드롭다운 조정
        if (this.$refs.memberAutocompleteRef) {
          const autocompleteElement = this.$refs.memberAutocompleteRef.$el;
          const panel = document.querySelector(".member-autocomplete-panel");

          if (autocompleteElement && panel) {
            const autocompleteWidth = autocompleteElement.offsetWidth;
            const autocompleteRect =
              autocompleteElement.getBoundingClientRect();

            panel.style.width = `${autocompleteWidth}px`;
            panel.style.left = `${autocompleteRect.left}px`;
            panel.style.top = `${autocompleteRect.bottom}px`;
          }
        }

        // 밴드 AutoComplete 드롭다운 조정
        if (this.$refs.bandAutocompleteRef) {
          const autocompleteElement = this.$refs.bandAutocompleteRef.$el;
          const panel = document.querySelector(".band-autocomplete-panel");

          if (autocompleteElement && panel) {
            const autocompleteWidth = autocompleteElement.offsetWidth;
            const autocompleteRect =
              autocompleteElement.getBoundingClientRect();

            panel.style.width = `${autocompleteWidth}px`;
            panel.style.left = `${autocompleteRect.left}px`;
            panel.style.top = `${autocompleteRect.bottom}px`;
          }
        }
      });
    },
  },
  mounted() {
    // 드롭다운 패널이 DOM에 추가될 때 너비 조정
    this.$nextTick(() => {
      const observer = new MutationObserver(() => {
        const memberPanel = document.querySelector(".member-autocomplete-panel");

        if (memberPanel && memberPanel.style.display !== "none") {
          this.adjustDropdownWidth();
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });
    });
  },
};
</script>

<style module>
/* Medium: 481px ~ (기본 스타일) */
.modalOverlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 900;
  padding: 0;
}

.modalContent {
  background: white;
  border-radius: 0;
  position: fixed;
  width: 100%;
  max-width: 1024px;
  height: 100%;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  max-height: 100vh;
  margin-bottom: 0;
  overflow-y: auto;
  box-shadow: 0 2px 16px rgba(0, 0, 0, 0.08);
  z-index: 1000;
  box-sizing: border-box;
}

.modalHeader {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 22px;
  border-bottom: 1px solid #e9ecef;
  position: sticky;
  top: 0;
  background: white;
  z-index: 10;
}

.modalTitle {
  margin: 0;
  font-size: 1.2rem;
  font-weight: 600;
  color: #333;
  font-family: "NanumSquareRoundB", sans-serif;
}

.closeButton {
  position: fixed;
  top: 12px;
  right: 12px;
  background: #fff;
  border: none;
  font-size: 24px;
  color: #999;
  cursor: pointer;
  padding: 4px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;
  z-index: 1001;
}

.closeButton:hover {
  background: #f1f3f4;
  color: #333;
}

.modalBody {
  padding: 20px;
}

.connectionStatus {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f8fff9;
  padding: 12px 16px;
  border-radius: 8px;
  border: 1px solid #d4edda;
  color: #28a745;
  font-family: "NanumSquareRoundR", sans-serif;
  font-size: 0.9rem;
  margin-bottom: 24px;
}

.statusIcon {
  font-size: 16px;
}

.userPreview {
  margin-bottom: 20px;
}

.previewCard {
  display: flex;
  align-items: center;
  gap: 16px;
  background: #f8f9fa;
  padding: 14px;
  border-radius: 10px;
  border: 1px solid #e9ecef;
}

.previewImage {
  flex-shrink: 0;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  overflow: hidden;
  background: #e9ecef;
  display: flex;
  align-items: center;
  justify-content: center;
}

.profileImage {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.noImage {
  font-size: 24px;
  color: #666;
}

.previewInfo {
  flex: 1;
  min-width: 0;
}

.previewNickname {
  font-size: 1rem;
  font-weight: 600;
  color: #333;
  font-family: "NanumSquareRoundB", sans-serif;
  margin-bottom: 4px;
}

.nicknameInput {
  width: 100%;
  padding: 8px 12px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  font-family: "NanumSquareRoundB", sans-serif;
  outline: none;
  transition: border-color 0.2s;
}

.nicknameInput:focus {
  border-color: #66bdff;
}

.previewEmail {
  font-size: 0.85rem;
  color: #666;
  font-family: "NanumSquareRoundR", sans-serif;
  margin-bottom: 8px;
  word-break: break-all;
}

.previewLink {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8rem;
  color: #66bdff;
  font-family: "NanumSquareRoundR", sans-serif;
  background: white;
  padding: 6px 12px;
  border-radius: 16px;
  border: 1px solid #66bdff;
  word-break: break-all;
}

.linkIcon {
  font-size: 14px;
  flex-shrink: 0;
}

.noLink {
  font-size: 0.8rem;
  color: #ff6b6b;
  font-family: "NanumSquareRoundR", sans-serif;
  background: #fff5f5;
  padding: 6px 12px;
  border-radius: 16px;
  border: 1px solid #ff6b6b;
}

.typeSection,
.cardSection {
  margin-bottom: 22px;
}

.typeSection:last-child,
.cardSection:last-child {
  margin-bottom: 0;
}

.sectionTitle {
  margin: 0 0 8px 0;
  font-size: 1rem;
  font-weight: 600;
  color: #333;
  font-family: "NanumSquareRoundB", sans-serif;
}

.subSectionTitle {
  margin: 0 0 8px 0;
  font-size: 0.95rem;
  font-weight: 500;
  color: #444;
  font-family: "NanumSquareRoundB", sans-serif;
}

.optionalText {
  font-size: 0.75rem;
  color: #999;
  font-weight: 400;
  font-family: "NanumSquareRoundR", sans-serif;
}

.descriptionSection {
  background: linear-gradient(135deg,
      rgba(102, 189, 255, 0.03),
      rgba(255, 107, 169, 0.03));
  padding: 16px;
  border-radius: 10px;
  border: 1px solid rgba(102, 189, 255, 0.15);
}

.descriptionInput {
  width: 100%;
  padding: 10px 14px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 0.85rem;
  font-family: "NanumSquareRoundR", sans-serif;
  transition: all 0.3s ease;
  background: white;
}

.descriptionInput:focus {
  outline: none;
  border-color: #66bdff;
  box-shadow: 0 0 0 3px rgba(102, 189, 255, 0.1);
  background: linear-gradient(135deg,
      rgba(102, 189, 255, 0.02),
      rgba(255, 107, 169, 0.02));
}

.descriptionInput:disabled {
  background: #f8f9fa;
  color: #6c757d;
  cursor: not-allowed;
}

.descriptionInput::placeholder {
  color: #adb5bd;
  font-style: italic;
}

.charCount {
  text-align: right;
  font-size: 0.75rem;
  color: #6c757d;
  font-family: "NanumSquareRoundR", sans-serif;
  margin-top: 6px;
  opacity: 0.7;
  transition: color 0.3s ease;
}

.charCountWarning {
  color: #ffc107 !important;
  opacity: 1;
  font-weight: 500;
}

.charCountDanger {
  color: #dc3545 !important;
  opacity: 1;
  font-weight: 600;
}

.categorySection,
.positionsSection,
.membersSection,
.descriptionSection,
/* SNS 링크 섹션 */
.snsSection {
  margin-bottom: 24px;
  background: linear-gradient(135deg, rgba(102, 189, 255, 0.03), rgba(255, 107, 169, 0.03));
  padding: 16px;
  border-radius: 10px;
  border: 1px solid rgba(102, 189, 255, 0.15);
}

.snsRow {
  display: flex;
  gap: 8px;
  margin-bottom: 10px;
  align-items: center;
}

.snsTypeLabel {
  flex-shrink: 0;
  min-width: 90px;
  padding: 10px 12px;
  background: linear-gradient(135deg, #66bdff, #ff6ba9);
  color: white;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 600;
  font-family: "NanumSquareRoundB", sans-serif;
  text-align: center;
}

.snsDropdownWrapper {
  position: relative;
  width: 100%;
}

.snsDropdownToggle {
  width: 100%;
  padding: 10px 14px;
  border: 2px dashed #dee2e6;
  border-radius: 8px;
  font-size: 0.85rem;
  font-family: "NanumSquareRoundR", sans-serif;
  background: white;
  color: #6c757d;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.snsDropdownToggle:hover:not(:disabled) {
  border-color: #66bdff;
  color: #66bdff;
}

.snsDropdownToggle:disabled {
  background: #f8f9fa;
  color: #adb5bd;
  cursor: not-allowed;
}

.dropdownArrow {
  font-size: 10px;
  transition: transform 0.2s ease;
}

.dropdownArrowOpen {
  transform: rotate(180deg);
}

.snsDropdownMenu {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 100;
  overflow: hidden;
}

.snsDropdownItem {
  width: 100%;
  padding: 10px 14px;
  border: none;
  background: white;
  text-align: left;
  font-size: 0.85rem;
  font-family: "NanumSquareRoundR", sans-serif;
  color: #333;
  cursor: pointer;
  transition: background 0.2s ease;
}

.snsDropdownItem:hover {
  background: #f0f7ff;
  color: #66bdff;
}

.snsDropdownItem:not(:last-child) {
  border-bottom: 1px solid #f0f0f0;
}

.snsInput {
  flex: 1;
  padding: 10px 14px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 0.85rem;
  font-family: "NanumSquareRoundR", sans-serif;
  transition: all 0.3s ease;
  background: white;
  box-sizing: border-box;
}

.snsInput:focus {
  outline: none;
  border-color: #66bdff;
  box-shadow: 0 0 0 3px rgba(102, 189, 255, 0.1);
}

.snsInput:disabled {
  background: #f8f9fa;
  color: #6c757d;
  cursor: not-allowed;
}

.snsInput::placeholder {
  color: #adb5bd;
  font-size: 0.8rem;
}

.snsRemoveBtn {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 6px;
  background: #ff6b6b;
  color: white;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.snsRemoveBtn:hover:not(:disabled) {
  background: #ee5a5a;
  transform: scale(1.05);
}

.snsRemoveBtn:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.membersSection {
  background: linear-gradient(135deg,
      rgba(102, 189, 255, 0.05),
      rgba(255, 107, 169, 0.05));
  padding: 16px;
  border-radius: 14px;
  border: 1px solid rgba(102, 189, 255, 0.2);
  position: relative;
  overflow: hidden;
}

.membersSection::before {
  content: "";
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: linear-gradient(45deg,
      transparent,
      rgba(102, 189, 255, 0.1),
      transparent);
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
}

.membersSection:hover::before {
  opacity: 1;
  animation: shimmer 2s infinite;
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%) translateY(-100%) rotate(45deg);
  }

  100% {
    transform: translateX(100%) translateY(100%) rotate(45deg);
  }
}

.radioGroup {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
}

.radioLabel {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-family: "NanumSquareRoundR", sans-serif;
  padding: 10px 16px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  transition: all 0.3s ease;
  background: white;
  flex: 1;
  justify-content: center;
}

.radioLabel:hover {
  border-color: #66bdff;
  background: rgba(102, 189, 255, 0.05);
}

.radioInput {
  margin: 0;
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: #66bdff;
}

.radioInput:checked+.radioText {
  color: #66bdff;
  font-weight: 600;
}

.radioLabel:has(.radioInput:checked) {
  border-color: #66bdff;
  background: linear-gradient(135deg, rgba(102, 189, 255, 0.1), rgba(255, 107, 169, 0.1));
  box-shadow: 0 2px 8px rgba(102, 189, 255, 0.2);
}

.radioText {
  font-size: 0.9rem;
  color: #333;
  transition: all 0.3s ease;
}

.checkboxGroup {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
}

.checkboxLabel {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  font-family: "NanumSquareRoundR", sans-serif;
  padding: 7px 10px;
  border-radius: 6px;
  transition: background-color 0.2s ease;
  min-width: fit-content;
  flex: 0 0 auto;
}

.checkboxLabel:hover {
  background-color: #f8f9fa;
}

.checkboxInput {
  width: 10px;
  margin: 0;
}

.checkboxText {
  min-width: 40px;
  font-size: 0.8rem;
  color: #333;
}

.prioritySection {
  margin-top: 16px;
  padding: 10px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.priorityTitle {
  margin: 0 0 12px 0;
  font-size: 0.85rem;
  font-weight: 500;
  color: #666;
  font-family: "NanumSquareRoundR", sans-serif;
}

.priorityList {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.priorityItem {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  background: white;
  padding: 7px 10px;
  border-radius: 6px;
  border: 1px solid #ddd;
}

.priorityNumber {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  background: linear-gradient(135deg, #66bdff, #ff6ba9);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 600;
}

.priorityItemContent {
  display: flex;
  align-items: center;
  gap: 8px;
}

.priorityLabel {
  min-width: 50px;
  font-size: 0.85rem;
  color: #333;
  font-family: "NanumSquareRoundR", sans-serif;
}

.priorityControls {
  display: flex;
  gap: 4px;
}

.priorityButton {
  background: white;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 0.75rem;
  color: #666;
  transition: all 0.3s ease;
  position: relative;
  z-index: 2;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.priorityButton:hover {
  background: #f8f9fa;
  border-color: #dee2e6;
  transform: scale(1.05);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.removeButton {
  background: linear-gradient(135deg, #ff6b6b, #ff5252);
  border-color: transparent;
  color: white;
  font-weight: bold;
  font-size: 0.8rem;
}

.removeButton:hover {
  background: linear-gradient(135deg, #ff5252, #ff4444);
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(255, 107, 107, 0.4);
}

.memberItem {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: linear-gradient(135deg,
      rgba(102, 189, 255, 0.1),
      rgba(255, 107, 169, 0.1));
  padding: 10px 14px;
  border-radius: 10px;
  border: 2px solid transparent;
  background-clip: padding-box;
  margin-bottom: 8px;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.memberItem::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #66bdff, #ff6ba9);
  opacity: 0;
  transition: opacity 0.3s ease;
  border-radius: 10px;
  z-index: -1;
}

.memberItem:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 189, 255, 0.3);
}

.memberItem:hover::before {
  opacity: 0.1;
}

.memberInfo {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  position: relative;
  z-index: 1;
}

.memberName {
  font-size: 0.9rem;
  color: #333;
  font-family: "NanumSquareRoundB", sans-serif;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
}

.memberName::before {
  content: "🎵";
  font-size: 0.75rem;
  opacity: 0.7;
}

.memberPosition {
  font-size: 0.75rem;
  color: #666;
  font-family: "NanumSquareRoundR", sans-serif;
  font-weight: 400;
  opacity: 0.8;
  margin-left: 18px;
}

.membersList {
  margin-bottom: 16px;
}

.noMembersMessage {
  text-align: center;
  padding: 24px 16px;
  margin-bottom: 16px;
}

.noMembersIcon {
  font-size: 2.5rem;
  margin-bottom: 12px;
  opacity: 0.6;
}

.noMembersText {
  font-size: 0.9rem;
  color: #666;
  font-family: "NanumSquareRoundB", sans-serif;
  margin: 0 0 6px 0;
  font-weight: 600;
}

.noMembersSubText {
  font-size: 0.8rem;
  color: #999;
  font-family: "NanumSquareRoundR", sans-serif;
  margin: 0;
  opacity: 0.8;
}

.memberInputContainer {
  display: flex;
  gap: 10px;
  margin-top: 16px;
  padding: 14px;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 10px;
  border: 1px solid rgba(102, 189, 255, 0.3);
  backdrop-filter: blur(10px);
}

.memberInput {
  flex: 1;
  border-radius: 6px;
  font-size: 0.85rem;
  font-family: "NanumSquareRoundR", sans-serif;
  transition: border-color 0.2s ease;
}

.memberInput:focus {
  outline: none;
  border-color: #66bdff;
}

.addMemberButton {
  background: linear-gradient(135deg, #66bdff, #ff6ba9);
  color: white;
  border: none;
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: "NanumSquareRoundB", sans-serif;
  white-space: nowrap;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(102, 189, 255, 0.3);
}

.addMemberButton::before {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  transform: translate(-50%, -50%);
  transition: all 0.3s ease;
}

.addMemberButton:hover:not(:disabled) {
  background: linear-gradient(135deg, #4a9eff, #ff4d94);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 189, 255, 0.4);
}

.addMemberButton:hover:not(:disabled)::before {
  width: 300px;
  height: 300px;
}

.addMemberButton:active:not(:disabled) {
  transform: translateY(0);
}

.addMemberButton:disabled {
  background: linear-gradient(135deg, #ccc, #bbb);
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

/* AutoComplete 공통 스타일 */
:global(.p-autocomplete) {
  width: 100%;
  display: flex;
  position: relative;
}

:global(.p-autocomplete-input) {
  border: 1px solid #ddd;
  border-right: none;
  border-radius: 6px 0px 0px 6px;
  padding: 10px 14px;
  height: 40px;
  font-size: 14px;
  width: 100%;
  box-sizing: border-box;
  font-family: "NanumSquareRoundR", sans-serif;
  flex: 1;
  transition: border-color 0.15s ease;
  background: #fff;
  color: #333;
}

:global(.p-autocomplete-input::placeholder) {
  color: #999;
}

:global(.p-autocomplete-input:focus) {
  border-color: #66bdff;
  border-right: none;
  outline: none;
  box-shadow: none;
}

:global(.p-autocomplete-input:focus)+ :global(.p-autocomplete-dropdown) {
  border-color: #66bdff;
  border-left: none;
}

:global(.p-autocomplete-dropdown) {
  border: 1px solid #ddd;
  border-left: none;
  border-radius: 0 6px 6px 0;
  background: #fff;
  height: 40px;
  color: #666;
  transition: border-color 0.15s ease;
  flex-shrink: 0;
  width: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

:global(.p-autocomplete-dropdown:hover) {
  background: #fff;
  border-color: #66bdff;
}

:global(.p-autocomplete-panel) {
  border: 1px solid #ddd;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  background: #fff;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  width: auto;
  min-width: 100%;
  margin-top: 2px;
}

:global(.member-autocomplete-panel) {
  z-index: 1200 !important;
}

:global(.p-autocomplete-item) {
  padding: 10px 14px;
  font-size: 14px;
  cursor: pointer;
  font-family: "NanumSquareRoundR", sans-serif;
  color: #333;
  transition: background-color 0.15s ease;
  border-bottom: 1px solid #f5f5f5;
}

:global(.p-autocomplete-item:last-child) {
  border-bottom: none;
}

:global(.p-autocomplete-item:hover) {
  background-color: #f8f9fa;
}

:global(.p-autocomplete-item-highlight) {
  background-color: #f0f7ff;
  color: #66bdff;
}

:global(.p-autocomplete-dropdown-icon) {
  color: #666;
  font-size: 12px;
  display: none;
}

:global(.p-autocomplete-loading-icon) {
  display: none;
}

:global(.p-autocomplete-empty-message) {
  padding: 0px 10px;
  font-size: 14px;
}

:global(.p-icon) {
  display: none;
}

.sectionDescription {
  margin: 0 0 16px 0;
  font-size: 0.9rem;
  color: #666;
  font-family: "NanumSquareRoundR", sans-serif;
  line-height: 1.4;
}

.typeDisplay {
  background: #f8f9fa;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  margin-bottom: 16px;
}

.typeDisplayText {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: "NanumSquareRoundR", sans-serif;
  font-size: 0.9rem;
}

.typeTag {
  background: linear-gradient(135deg, #66bdff, #ff6ba9);
  color: white;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 500;
}

.typeSelectContainer {
  width: 100%;
}

.typeDropdown {
  width: 100%;
}

.typeDropdown :global(.p-dropdown) {
  width: 100%;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-family: "NanumSquareRoundR", sans-serif;
}

.typeDropdown :global(.p-dropdown:not(.p-disabled):hover) {
  border-color: #66bdff;
}

.typeDropdown :global(.p-dropdown:not(.p-disabled).p-focus) {
  outline: none;
  border-color: #66bdff;
  box-shadow: 0 0 0 2px rgba(102, 189, 255, 0.2);
}

.cardButton {
  width: 100%;
  background: linear-gradient(135deg, #66bdff, #ff6ba9);
  color: white;
  border: none;
  padding: 14px 20px;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: "NanumSquareRoundR", sans-serif;
}

.cardButton:hover:not(:disabled) {
  background: linear-gradient(135deg, #4a9eff, #ff4d94);
  transform: translateY(-1px);
}

.cardButton:disabled {
  background: #ccc;
  cursor: not-allowed;
  transform: none;
}

.updateButton {
  background: linear-gradient(135deg, #28a745, #20c997);
}

.updateButton:hover:not(:disabled) {
  background: linear-gradient(135deg, #218838, #1aa179);
}

/* ================================================
   반응형 디자인
   - Medium: 481px ~ (기본 스타일, 최대 1024px)
   - Small: ~ 480px
   ================================================ */

/* Small (~ 480px) */
@media (max-width: 480px) {
  .modalContent {
    box-shadow: none;
  }

  .modalHeader {
    padding: 14px 16px;
  }

  .modalTitle {
    font-size: 1.1rem;
  }

  .closeButton {
    top: 10px;
    right: 10px;
    font-size: 22px;
    width: 32px;
    height: 32px;
  }

  .modalBody {
    padding: 16px;
  }

  .userPreview {
    margin-bottom: 16px;
  }

  .previewCard {
    flex-direction: column;
    text-align: center;
    gap: 12px;
    padding: 12px;
    border-radius: 8px;
  }

  .previewImage {
    width: 50px;
    height: 50px;
  }

  .previewInfo {
    width: 100%;
  }

  .previewNickname {
    font-size: 0.95rem;
  }

  .previewLink,
  .noLink {
    justify-content: center;
    font-size: 0.75rem;
    padding: 5px 10px;
  }

  .sectionTitle {
    font-size: 0.95rem;
    margin-bottom: 6px;
  }

  .subSectionTitle {
    font-size: 0.9rem;
    margin-bottom: 6px;
  }

  .sectionDescription {
    font-size: 0.8rem;
    margin-bottom: 12px;
  }

  .typeSection,
  .cardSection {
    margin-bottom: 18px;
  }

  .typeDisplay {
    padding: 12px;
    margin-bottom: 12px;
  }

  .typeDisplayText {
    font-size: 0.85rem;
    flex-wrap: wrap;
  }

  .typeTag {
    font-size: 0.75rem;
    padding: 3px 10px;
  }

  .radioGroup {
    flex-direction: column;
    gap: 10px;
  }

  .radioText {
    font-size: 0.85rem;
  }

  .checkboxGroup {
    gap: 4px;
  }

  .checkboxLabel {
    padding: 6px 8px;
  }

  .checkboxText {
    font-size: 0.8rem;
    min-width: 35px;
  }

  .prioritySection {
    margin-top: 12px;
    padding: 10px;
  }

  .priorityTitle {
    font-size: 0.8rem;
    margin-bottom: 10px;
  }

  .priorityList {
    gap: 5px;
  }

  .priorityItem {
    padding: 6px 10px;
    gap: 6px;
  }

  .priorityNumber {
    width: 18px;
    height: 18px;
    font-size: 0.7rem;
  }

  .priorityLabel {
    font-size: 0.8rem;
    min-width: 40px;
  }

  .priorityButton {
    width: 24px;
    height: 24px;
    font-size: 0.7rem;
    border-radius: 6px;
  }

  .membersSection {
    padding: 14px;
    border-radius: 12px;
  }

  .membersList {
    margin-bottom: 12px;
  }

  .memberItem {
    padding: 10px 12px;
    border-radius: 8px;
    margin-bottom: 6px;
  }

  .memberName {
    font-size: 0.8rem;
  }

  .memberName::before {
    font-size: 0.7rem;
  }

  .memberPosition {
    font-size: 0.7rem;
    margin-left: 14px;
  }

  .noMembersMessage {
    padding: 16px 12px;
    margin-bottom: 12px;
  }

  .noMembersIcon {
    font-size: 2rem;
    margin-bottom: 8px;
  }

  .noMembersText {
    font-size: 0.85rem;
  }

  .noMembersSubText {
    font-size: 0.75rem;
  }

  .memberInputContainer {
    flex-direction: column;
    padding: 12px;
    gap: 10px;
    margin-top: 12px;
    border-radius: 8px;
  }

  .memberInput {
    padding: 10px;
    font-size: 0.8rem;
  }

  .addMemberButton {
    padding: 12px 16px;
    font-size: 0.85rem;
    border-radius: 8px;
    width: 100%;
  }

  .categorySection,
  .positionsSection,
  .membersSection,
  .descriptionSection,
  .snsSection {
    margin-bottom: 16px;
    padding: 14px;
    border-radius: 8px;
  }

  .snsRow {
    gap: 6px;
    margin-bottom: 8px;
  }

  .snsTypeLabel {
    min-width: 75px;
    padding: 8px 10px;
    font-size: 0.75rem;
    border-radius: 6px;
  }

  .snsInput {
    padding: 8px 10px;
    font-size: 0.8rem;
    border-radius: 6px;
  }

  .snsInput::placeholder {
    font-size: 0.75rem;
  }

  .snsRemoveBtn {
    width: 28px;
    height: 28px;
    font-size: 12px;
    border-radius: 5px;
  }

  .snsDropdownToggle {
    padding: 8px 10px;
    font-size: 0.8rem;
    border-radius: 6px;
  }

  .snsDropdownMenu {
    border-radius: 6px;
  }

  .snsDropdownItem {
    padding: 8px 10px;
    font-size: 0.8rem;
  }

  .descriptionSection {
    padding: 14px;
    border-radius: 10px;
  }

  .descriptionInput {
    padding: 10px 12px;
    font-size: 0.85rem;
    border-radius: 6px;
  }

  .charCount {
    font-size: 0.7rem;
    margin-top: 4px;
  }

  .cardButton {
    padding: 12px 16px;
    font-size: 0.9rem;
    border-radius: 10px;
  }

  .connectionStatus {
    padding: 10px 14px;
    font-size: 0.85rem;
    margin-bottom: 18px;
  }

  .statusIcon {
    font-size: 14px;
  }

  /* AutoComplete 스타일 조정 */
  .memberAutocomplete :global(.p-autocomplete .p-inputtext) {
    padding: 10px;
    font-size: 0.8rem;
  }

  .memberAutocomplete :global(.p-autocomplete-items .p-autocomplete-item) {
    padding: 8px 10px;
    font-size: 0.8rem;
  }

  :global(.p-autocomplete-input) {
    padding: 8px 12px;
    height: 38px;
    border-radius: 6px 0 0 6px;
  }

  :global(.p-autocomplete-dropdown) {
    height: 38px;
    width: 38px;
    border-radius: 0 6px 6px 0;
  }

  :global(.p-autocomplete-panel) {
    max-height: 180px;
  }

  :global(.p-autocomplete-item) {
    padding: 8px 12px;
    font-size: 13px;
  }
}
</style>
